import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  isShapeFrozenForGeometry,
  isShapeLockedForUserEdit,
} from '@/lib/groups/element-edit-policy'

import type { ChainPeerState } from '../types/polled-canvas'

/**
 * Chain peers: every member of a chain the selection touches, minus the selected shapes
 * themselves — without that subtraction the peer chrome double-draws over the real chrome.
 * Suppressed outright while a caret is in a frame: chain chrome during inline editing would
 * frame the very text being typed into.
 */
export function resolveChainPeerIds(
  chainFrameIds: ReadonlyMap<string, string[]> | undefined,
  selectedIds: string[],
  editingShapeTextId: string | null
): Set<string> {
  const chainPeerIds = new Set<string>()

  if (chainFrameIds && chainFrameIds.size > 0 && !editingShapeTextId) {
    for (const id of selectedIds) {
      for (const memberId of chainFrameIds.get(id) ?? []) chainPeerIds.add(memberId)
    }

    for (const id of selectedIds) chainPeerIds.delete(id)
  }

  return chainPeerIds
}

/** Lock/freeze for each chain peer, keyed by shape id. */
export function resolveChainPeerStateById(
  chainPeerShapes: ShapeData[],
  shapes: ShapeData[]
): Map<string, ChainPeerState> {
  const chainPeerStateById = new Map<string, ChainPeerState>()

  if (chainPeerShapes.length > 0) {
    // Built from all `shapes` (the poll's `next`), not `visible`: the ancestry walk has to see a
    // hidden ancestor group, and a peer inside one would otherwise resolve as unlocked.
    const shapesById = new Map(shapes.map((shape) => [shape.id, shape]))

    for (const peer of chainPeerShapes) {
      // Resolved through group ancestry rather than read off `peer.locked`/`peer.frozen`: a
      // child of a locked group is locked without carrying the flag, and chrome that colored
      // it blue would invite a gesture the manager then refuses.
      const locked = isShapeLockedForUserEdit(peer, shapesById)

      chainPeerStateById.set(peer.id, {
        locked,
        frozenOnly: !locked && isShapeFrozenForGeometry(peer, shapesById),
      })
    }
  }

  return chainPeerStateById
}
